import type { MilestoneView } from "@plannello/shared";

/** Splits "1.10.2-beta" into comparable parts: numbers compare numerically, the rest as text. */
function versionParts(v: string): Array<number | string> {
  return v
    .replace(/^v/i, "")
    .split(/[.\-+ ]/)
    .map((p) => (/^\d+$/.test(p) ? Number(p) : p.toLowerCase()));
}

/** Natural version order: 0.9.0 < 0.10.0 < 1.0.0. Falls back to locale order for names. */
export function compareVersions(a: string, b: string): number {
  const pa = versionParts(a);
  const pb = versionParts(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i];
    const y = pb[i];
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    if (x === y) continue;
    if (typeof x === "number" && typeof y === "number") return x - y;
    return String(x).localeCompare(String(y));
  }
  return 0;
}

const STATUS_ORDER = { open: 0, released: 1, closed: 2 } as const;

/**
 * Planning order: open milestones first, by target date (undated after dated), then by version
 * so the next release comes first; released and closed after that, newest first.
 */
export function compareMilestones(a: MilestoneView, b: MilestoneView): number {
  const s = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
  if (s !== 0) return s;
  const label = (m: MilestoneView) => m.version ?? m.name;
  if (a.status === "open") {
    if (a.targetDate && b.targetDate && a.targetDate !== b.targetDate) return a.targetDate < b.targetDate ? -1 : 1;
    if (a.targetDate && !b.targetDate) return -1;
    if (!a.targetDate && b.targetDate) return 1;
    return compareVersions(label(a), label(b));
  }
  const ra = a.releasedAt ?? a.targetDate ?? "";
  const rb = b.releasedAt ?? b.targetDate ?? "";
  if (ra !== rb) return ra < rb ? 1 : -1;
  return compareVersions(label(b), label(a));
}

/** "Spring release (1.4)" or just "0.10.0" when name and version are the same. */
export function milestoneLabel(m: Pick<MilestoneView, "name" | "version">): string {
  return m.version && m.version !== m.name ? `${m.name} (${m.version})` : m.name;
}
